import React from "react";
import PropTypes from "prop-types";
import styles from "./cart-drawer-upsell-skeleton.module.css";
import stripStyles from "./cart-drawer-upsell.module.css";

function ShimmerBar({ className }) {
  return <span className={`${styles.shimmer} ${className || ""}`.trim()} aria-hidden />;
}

function HorizontalCardSkeleton() {
  return (
    <div className={styles.horizontalCard}>
      <ShimmerBar className={styles.horizontalThumb} />
      <div className={styles.horizontalMeta}>
        <ShimmerBar className={styles.lineWide} />
        <ShimmerBar className={styles.lineMid} />
      </div>
      <ShimmerBar className={styles.horizontalBtn} />
    </div>
  );
}

function CarouselCardSkeleton() {
  return (
    <div className={styles.carouselCard}>
      <ShimmerBar className={styles.carouselImage} />
      <div className={styles.carouselMeta}>
        <ShimmerBar className={styles.lineWide} />
        <ShimmerBar className={styles.lineShort} />
        <ShimmerBar className={styles.carouselBtn} />
      </div>
    </div>
  );
}

function CheckboxStackRowSkeleton() {
  return (
    <div className={styles.checkboxStackRow}>
      <ShimmerBar className={styles.checkboxBox} />
      <HorizontalCardSkeleton />
    </div>
  );
}

function CheckboxStackSkeleton({ rows = 1 }) {
  const safeRows = Math.max(1, Math.min(Number(rows) || 1, 4));
  return (
    <div className={styles.checkboxStackColumn}>
      {Array.from({ length: safeRows }, (_, index) => (
        <CheckboxStackRowSkeleton key={index} />
      ))}
    </div>
  );
}

export default function CartDrawerUpsellSkeleton({
  showHeading = true,
  isCarousel = false,
  isCheckboxStack = false,
  showAsGrid = false,
  itemsPerRow = 3,
  productsShownAtOnce = 1,
  count = 3,
  surface = "cart-drawer",
}) {
  const safeCount = Math.max(1, Math.min(Number(count) || 1, 6));
  const visibleCards = isCarousel
    ? showAsGrid
      ? safeCount
      : Math.max(1, Math.min(Number(itemsPerRow) || 3, safeCount))
    : 1;
  const isProductPage = surface === "product-page";
  const carouselTrackClassName = isCarousel
    ? `${isProductPage ? stripStyles.carouselTrack : styles.carouselTrack}${
        showAsGrid && isProductPage ? ` ${stripStyles.carouselTrackGrid}` : ""
      }${
        !showAsGrid &&
        isProductPage &&
        visibleCards <= (Number(itemsPerRow) || 3)
          ? ` ${stripStyles.carouselTrackSparse}`
          : ""
      }`
    : "";
  const carouselColClassName = isCarousel
    ? isProductPage
      ? stripStyles.carouselCol
      : styles.carouselCol
    : "";

  return (
    <div
      className={`${styles.wrap}${surface === "product-page" ? ` ${styles.wrapProductPage}` : ""}`}
      role="status"
      aria-live="polite"
      aria-label="Loading product recommendations"
    >
      {showHeading ? <ShimmerBar className={styles.heading} /> : null}
      {isCheckboxStack ? (
        <div className={stripStyles.checkboxStackTrack}>
          <div className={stripStyles.checkboxStackSlide}>
            <CheckboxStackSkeleton rows={productsShownAtOnce} />
          </div>
        </div>
      ) : isCarousel ? (
        <div
          className={carouselTrackClassName}
          style={
            isProductPage
              ? {
                  "--pp-items-per-row": Number(itemsPerRow) || 3,
                  "--pp-grid-cols": Number(itemsPerRow) || 3,
                  "--pp-card-min": "155px",
                  "--pp-card-max": "340px",
                  "--pp-card-count": visibleCards,
                }
              : { "--pp-items-per-row": visibleCards }
          }
        >
          {Array.from({ length: visibleCards }, (_, index) => (
            <div key={index} className={carouselColClassName}>
              <CarouselCardSkeleton />
            </div>
          ))}
        </div>
      ) : (
        <HorizontalCardSkeleton />
      )}
    </div>
  );
}

CartDrawerUpsellSkeleton.propTypes = {
  showHeading: PropTypes.bool,
  isCarousel: PropTypes.bool,
  isCheckboxStack: PropTypes.bool,
  showAsGrid: PropTypes.bool,
  itemsPerRow: PropTypes.number,
  productsShownAtOnce: PropTypes.number,
  count: PropTypes.number,
  surface: PropTypes.oneOf(["cart-drawer", "product-page"]),
};
